import { useGcpProjects } from '@/query/gcp';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../ui/toast';
import { getProjectErrorToast } from './gcp-project-picker.domain';

interface UseGcpProjectPickerArgs {
  onProjectSelect: (projectId: string) => void;
}

export function useGcpProjectPicker({ onProjectSelect }: UseGcpProjectPickerArgs) {
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const { toast } = useToast();
  const { data, isLoading, error, isError, refetch } = useGcpProjects();

  const projects = data?.projects ?? [];
  const errorMessage = error instanceof Error ? error.message : error ? String(error) : null;

  useEffect(() => {
    if (isError && errorMessage) {
      const { title, description } = getProjectErrorToast(errorMessage);
      toast({
        title,
        description,
        variant: 'destructive',
      });
    }
  }, [isError, errorMessage, toast]);

  const handleSelect = useCallback(() => {
    if (selectedProjectId) {
      onProjectSelect(selectedProjectId);
    }
  }, [selectedProjectId, onProjectSelect]);

  const handleItemClick = useCallback(
    (projectId: string) => {
      if (selectedProjectId === projectId) {
        handleSelect();
        return;
      }
      setSelectedProjectId(projectId);
    },
    [selectedProjectId, handleSelect]
  );

  return {
    loading: isLoading,
    error: errorMessage,
    projects,
    selectedProjectId,
    fetchProjects: refetch,
    handleSelect,
    handleItemClick,
  };
}
